import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Shield, TrendingUp, Coins, Lock, CheckCircle2, ArrowRight,
  MessageCircle, Handshake, Users, Sparkles, Phone, Mail, MapPin, FileText, Clock, Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1b66fcb6-686c-496a-809e-79f0cfe50fdc/images/b1bef6b3a99f27cd1963244f1b4592b5f46fc5a31baf34a6a7e3f6f46ff9f19e.png";
// TODO: sostituisci con il tuo numero WhatsApp reale (formato internazionale senza + e senza spazi)
const WHATSAPP = "393497173896";
const waLink = (msg) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;

const CASI = [
  { nome: "Marco", ruolo: "Libero professionista", da: "32.000 €", a: "49.600 €", perc: "+55%" },
  { nome: "Matteo", ruolo: "Padre di famiglia", da: "50.000 €", a: "62.000 €", perc: "+24%" },
  { nome: "Paolo", ruolo: "Imprenditore", da: "89.000 €", a: "124.000 €", perc: "+39%" },
];

const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-mono tracking-[0.25em] uppercase border border-yellow-700/50 bg-yellow-500/5 text-yellow-500">
    {children}
  </span>
);

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`px-5 md:px-8 py-16 md:py-24 max-w-6xl mx-auto ${className}`}>{children}</section>
);

function LeadForm({ tipo, testid }) {
  const isCliente = tipo === "cliente";
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", patrimonio: "", messaggio: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email) { toast.error("Nome ed email sono obbligatori"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { tipo, ...form });
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontatto io a breve.");
      setForm({ nome: "", email: "", telefono: "", patrimonio: "", messaggio: "" });
    } catch {
      toast.error("Errore nell'invio. Riprova o scrivimi su WhatsApp.");
    } finally { setSending(false); }
  };

  const input = "w-full bg-zinc-950 border border-zinc-800 rounded-none text-zinc-50 placeholder-zinc-600 px-4 py-3 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500 transition-colors";

  if (done) {
    return (
      <div className="border border-emerald-700/40 bg-emerald-500/5 p-8 text-center" data-testid={`${testid}-success`}>
        <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-3" />
        <h3 className="font-head font-bold text-xl text-zinc-50">Richiesta ricevuta ✅</h3>
        <p className="text-zinc-400 mt-2">Ti ricontatto personalmente entro 24-48h. Nel frattempo puoi scrivermi su WhatsApp.</p>
        <a href={waLink(isCliente ? "Ciao Alex, ho compilato il form. Vorrei la consulenza gratuita." : "Ciao Alex, mi candido come collaboratore/partner.")}
          target="_blank" rel="noopener noreferrer" data-testid={`${testid}-wa-after`}
          className="inline-flex items-center gap-2 mt-4 bg-[#25D366] text-black font-semibold px-5 py-2.5 hover:opacity-90 transition-opacity">
          <MessageCircle size={16} /> Scrivimi su WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={testid}>
      <input className={input} placeholder="Nome e cognome *" value={form.nome}
        onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid={`${testid}-nome`} />
      <input className={input} type="email" placeholder="Email *" value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${testid}-email`} />
      <input className={input} placeholder="Telefono / WhatsApp" value={form.telefono}
        onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid={`${testid}-telefono`} />
      {isCliente && (
        <select className={input} value={form.patrimonio}
          onChange={(e) => setForm({ ...form, patrimonio: e.target.value })} data-testid={`${testid}-patrimonio`}>
          <option value="">Patrimonio da proteggere (facoltativo)</option>
          <option>Fino a 50.000 €</option>
          <option>50.000 € - 150.000 €</option>
          <option>150.000 € - 500.000 €</option>
          <option>Oltre 500.000 €</option>
        </select>
      )}
      <textarea className={`${input} min-h-[90px]`} placeholder={isCliente ? "Raccontami la tua situazione (facoltativo)" : "Parlami di te e della tua esperienza (facoltativo)"}
        value={form.messaggio} onChange={(e) => setForm({ ...form, messaggio: e.target.value })} data-testid={`${testid}-messaggio`} />
      <button type="submit" disabled={sending} data-testid={`${testid}-submit`}
        className="w-full flex items-center justify-center gap-2 bg-yellow-500 text-black font-bold py-3.5 hover:bg-yellow-400 disabled:opacity-50 transition-colors">
        {sending ? "Invio..." : isCliente ? "PRENOTA LA CONSULENZA GRATUITA" : "CANDIDATI COME PARTNER"} <ArrowRight size={18} />
      </button>
      <a href={waLink(isCliente ? "Ciao Alex, vorrei la consulenza patrimoniale gratuita." : "Ciao Alex, mi candido come consulente/partner affiliato.")}
        target="_blank" rel="noopener noreferrer" data-testid={`${testid}-wa`}
        className="w-full flex items-center justify-center gap-2 border border-[#25D366] text-[#25D366] font-semibold py-3 hover:bg-[#25D366]/10 transition-colors">
        <MessageCircle size={18} /> Oppure scrivimi su WhatsApp
      </a>
    </form>
  );
}

export default function Portale() {
  return (
    <div className="bg-zinc-950 text-zinc-50 min-h-screen font-body">
      <Toaster theme="dark" position="top-right" />

      {/* Nav */}
      <nav className="border-b border-zinc-900 px-5 md:px-8 py-4 flex items-center justify-between sticky top-0 bg-zinc-950/95 backdrop-blur z-30">
        <div className="flex items-center gap-2.5">
          <img src="/alex-gold.jpg" alt="Alex Gold - Protezione Patrimoniale"
            className="w-10 h-10 rounded-full object-cover object-top border-2 border-yellow-500" data-testid="nav-avatar" />
          <div className="leading-none">
            <p className="font-head font-extrabold tracking-tight">ALEX GOLD</p>
            <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500">Protezione Patrimonio</p>
          </div>
        </div>
        <a href="#contatti" data-testid="nav-cta" className="bg-yellow-500 text-black text-sm font-bold px-4 py-2 hover:bg-yellow-400 transition-colors">
          Consulenza gratuita
        </a>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden border-b border-zinc-900">
        <img src={HERO_IMG} alt="oro fisico" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/40" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-24 md:py-36">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <Badge><Sparkles size={11} /> Consulenza Patrimoniale</Badge>
            <h1 className="font-head font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.05]">
              Metti il tuo patrimonio <span className="text-yellow-500">al sicuro</span>, fuori dal sistema bancario.
            </h1>
            <p className="text-lg text-zinc-300 mt-5 leading-relaxed">
              Aiuto imprenditori, professionisti e famiglie a proteggere e diversificare il patrimonio con l'<strong className="text-yellow-500">oro fisico</strong> — un bene reale, tuo, che nessuno può bloccare.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a href="#contatti" data-testid="hero-cta-primary" className="inline-flex items-center justify-center gap-2 bg-yellow-500 text-black font-bold px-6 py-3.5 hover:bg-yellow-400 transition-colors">
                Prenota la consulenza gratuita <ArrowRight size={18} />
              </a>
              <a href={waLink("Ciao Alex, vorrei informazioni sulla protezione patrimoniale con l'oro fisico.")} target="_blank" rel="noopener noreferrer"
                data-testid="hero-cta-wa" className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-100 font-semibold px-6 py-3.5 hover:border-yellow-500 hover:text-yellow-500 transition-colors">
                <MessageCircle size={18} /> Scrivimi su WhatsApp
              </a>
            </div>
            <div className="flex flex-wrap gap-6 mt-10 text-sm text-zinc-400">
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-yellow-500" /> +150 clienti protetti</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-yellow-500" /> Fuori dal sistema bancario</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-yellow-500" /> Strategia su misura</span>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Problema */}
      <Section>
        <Badge>Il problema</Badge>
        <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight mt-4 max-w-3xl">
          Lasciare tutto in banca non è sicuro. È perdere lentamente.
        </h2>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            { icon: TrendingUp, t: "L'inflazione erode", d: "Ogni anno il potere d'acquisto dei tuoi risparmi fermi in banca diminuisce, silenziosamente." },
            { icon: Lock, t: "Dipendi dal sistema", d: "Conti bloccabili, bail-in, controlli: se è tutto dentro il sistema, non è davvero tuo." },
            { icon: Shield, t: "Patrimonio esposto", d: "Imprenditori e P.IVA con tutto intestato a sé rischiano casa e risparmi al primo problema." },
          ].map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-zinc-900 border border-zinc-800 p-6">
              <c.icon size={26} className="text-yellow-500 mb-3" />
              <h3 className="font-head font-semibold text-lg">{c.t}</h3>
              <p className="text-zinc-400 mt-2 text-sm leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Soluzione */}
      <Section className="border-y border-zinc-900">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge><Coins size={11} /> La soluzione</Badge>
            <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight mt-4">
              L'oro fisico: la tua riserva reale, fuori dal sistema.
            </h2>
            <p className="text-zinc-300 mt-4 leading-relaxed">
              Non è speculazione. È protezione. Una quota del tuo patrimonio in oro fisico — custodito in modo sicuro — che nessuno può bloccarti con un click.
            </p>
            <ul className="space-y-3 mt-6">
              {["Bene reale, tuo, tangibile", "Storicamente protegge dall'inflazione", "Diversificazione fuori dalle banche", "Strategia su misura sul tuo patrimonio"].map((x, i) => (
                <li key={i} className="flex items-center gap-3 text-zinc-200"><CheckCircle2 size={18} className="text-yellow-500 shrink-0" /> {x}</li>
              ))}
            </ul>
          </div>
          <img src={HERO_IMG} alt="lingotti d'oro" className="w-full h-72 md:h-80 object-cover border border-zinc-800" />
        </div>
      </Section>

      {/* Casi studio */}
      <Section>
        <Badge>Risultati reali</Badge>
        <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight mt-4">I risultati dei miei clienti</h2>
        <p className="text-zinc-400 mt-2">Casi reali a scopo illustrativo. I rendimenti passati non garantiscono risultati futuri.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {CASI.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-zinc-900 border border-zinc-800 p-6 relative overflow-hidden" data-testid="caso-card">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{c.ruolo}</p>
              <h3 className="font-head font-extrabold text-2xl mt-1">{c.nome}</h3>
              <div className="flex items-center gap-2 mt-4 text-sm text-zinc-400">
                <span>{c.da}</span> <ArrowRight size={14} className="text-yellow-500" /> <span className="text-zinc-100 font-semibold">{c.a}</span>
              </div>
              <p className="font-head font-extrabold text-3xl text-yellow-500 mt-3">{c.perc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Perché funziona */}
      <Section className="border-t border-zinc-900">
        <Badge><Sparkles size={11} /> Perché funziona</Badge>
        <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight mt-4 max-w-3xl">
          Un'offerta pensata per proteggerti davvero, senza rischi né pensieri.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {[
            { icon: TrendingUp, label: "Il risultato", t: "Da liquidità ferma a oro reale", d: "Proteggi il potere d'acquisto e lasci ai tuoi figli un bene tangibile, tuo." },
            { icon: Users, label: "La prova", t: "Non sei il primo", d: "10 anni di esperienza, +150 patrimoni affiancati e casi reali documentati." },
            { icon: Clock, label: "Subito", t: "Risultato immediato", d: "Scopri quanto perdi in 30 secondi. E inizi anche con soli 5.000 €." },
            { icon: Handshake, label: "Zero pensieri", t: "Faccio tutto io", d: "Analisi, scelta dei lingotti, custodia assicurata e burocrazia: a tutto penso io." },
          ].map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-zinc-900 border border-zinc-800 p-6" data-testid="perche-card">
              <c.icon size={26} className="text-yellow-500 mb-3" />
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-600">{c.label}</p>
              <h3 className="font-head font-semibold text-lg mt-1">{c.t}</h3>
              <p className="text-zinc-400 mt-2 text-sm leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8">
          <a href="#contatti" data-testid="perche-cta" className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold px-6 py-3.5 hover:bg-yellow-400 transition-colors">
            Prenota la consulenza gratuita <ArrowRight size={18} />
          </a>
        </div>
      </Section>

      {/* Come funziona */}
      <Section className="border-y border-zinc-900">
        <Badge>Come funziona</Badge>
        <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight mt-4">3 passi per blindare il tuo patrimonio</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            { n: "01", t: "Analisi gratuita", d: "Prenoti una consulenza conoscitiva e analizziamo insieme il tuo livello di rischio." },
            { n: "02", t: "Strategia su misura", d: "Costruiamo un piano di protezione e diversificazione adatto alla tua situazione." },
            { n: "03", t: "Patrimonio protetto", d: "Metti al sicuro ciò che hai costruito, con una riserva reale fuori dal sistema." },
          ].map((s, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 p-6">
              <p className="font-head font-extrabold text-4xl text-yellow-500/30">{s.n}</p>
              <h3 className="font-head font-semibold text-lg mt-2">{s.t}</h3>
              <p className="text-zinc-400 mt-2 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Contatti / Form cliente */}
      <Section id="contatti">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <Badge><Shield size={11} /> Per te</Badge>
            <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight mt-4">Prenota la tua consulenza gratuita</h2>
            <p className="text-zinc-300 mt-4 leading-relaxed">
              Analisi conoscitiva senza impegno. Ti dico esattamente quanta parte del tuo patrimonio ha senso proteggere e come. Ti ricontatto personalmente.
            </p>
            <div className="mt-6 space-y-2 text-sm text-zinc-400">
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-yellow-500" /> Nessun impegno, nessun costo</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-yellow-500" /> Risposta entro 24-48h</span>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8">
            <LeadForm tipo="cliente" testid="form-cliente" />
          </div>
        </div>
      </Section>

      {/* Collaboratori */}
      <Section id="collabora" className="border-t border-zinc-900">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8 order-2 md:order-1">
            <LeadForm tipo="collaboratore" testid="form-collaboratore" />
          </div>
          <div className="order-1 md:order-2">
            <Badge><Handshake size={11} /> Lavora con me</Badge>
            <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight mt-4">Diventa consulente / partner affiliato</h2>
            <p className="text-zinc-300 mt-4 leading-relaxed">
              Cerchi un'attività concreta nel settore della protezione patrimoniale? Entra nel network: ti formo, ti do il metodo e guadagni portando valore ai tuoi contatti.
            </p>
            <ul className="space-y-3 mt-6">
              {[
                { icon: Users, t: "Formazione e metodo inclusi" },
                { icon: Coins, t: "Guadagni su ogni cliente portato" },
                { icon: TrendingUp, t: "Settore in forte crescita" },
              ].map((x, i) => (
                <li key={i} className="flex items-center gap-3 text-zinc-200"><x.icon size={18} className="text-yellow-500 shrink-0" /> {x.t}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 px-5 md:px-8 py-12 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between gap-8">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="bg-yellow-500 p-1.5"><Shield size={18} className="text-black" /></div>
              <p className="font-head font-extrabold tracking-tight">ALEX GOLD | Protezione Patrimonio</p>
            </div>
            <p className="text-zinc-500 text-sm mt-3 max-w-sm">Protezione e diversificazione patrimoniale con l'oro fisico, per imprenditori, professionisti e famiglie.</p>
            <Link to="/presentazione" data-testid="footer-professionisti"
              className="inline-flex items-center gap-1.5 mt-4 text-xs font-mono uppercase tracking-[0.15em] text-yellow-600/80 hover:text-yellow-500 transition-colors border-b border-yellow-700/30 hover:border-yellow-500 pb-0.5">
              <FileText size={12} /> Documento per professionisti
            </Link>
          </div>
          <div className="text-sm text-zinc-400 space-y-2">
            <a href="mailto:alassane-diallo@libero.it" className="flex items-center gap-2 hover:text-yellow-500 transition-colors" data-testid="footer-email"><Mail size={15} className="text-yellow-500" /> alassane-diallo@libero.it</a>
            <a href="tel:+393497173896" className="flex items-center gap-2 hover:text-yellow-500 transition-colors" data-testid="footer-phone"><Phone size={15} className="text-yellow-500" /> +39 349 717 3896</a>
            <a href={waLink("Ciao Alex, vorrei informazioni sulla protezione patrimoniale.")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#25D366] transition-colors" data-testid="footer-whatsapp"><MessageCircle size={15} className="text-[#25D366]" /> WhatsApp: +39 349 717 3896</a>
            <p className="flex items-center gap-2"><MapPin size={15} className="text-yellow-500" /> Italia</p>
          </div>
        </div>
        <p className="text-[11px] text-zinc-600 mt-10 border-t border-zinc-900 pt-6 leading-relaxed">
          Caso studio e risultati a scopo illustrativo. La rivalutazione dipende dal prezzo dell'oro; i rendimenti passati non garantiscono risultati futuri. Le informazioni presenti non costituiscono consulenza finanziaria. © {new Date().getFullYear()} Alex Gold.
        </p>
      </footer>
    </div>
  );
}
